import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Music2, Building, ArrowRight, Star, GraduationCap } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const FREE_BULLETS = [
  "Musician profile with full bio + playing standard",
  "Availability diary with monthly view & weekly repeats",
  "Apply to unlimited gigs on the gig board",
  "Receive booking requests, accept / decline / maybe",
  "In-app messaging on every booking",
  "Live webcal:// calendar feed",
  "Join unlimited ensembles",
  "Create 1 ensemble (up to 5 future rehearsals)",
];

const PLATINUM_BULLETS = [
  "Unlimited ensembles and members",
  "Unlimited rehearsals + attendance tracking",
  "Document & sheet-music uploads",
  "Post unlimited gigs to the gig board",
  "Broadcast messages to your ensemble",
  "Player database & contact export",
  "Dep finder & emergency dep alerts",
  "Section-leader & manager admin roles",
  "Verified ensemble / org badge",
  "Priority support",
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billing, setBilling] = useState("monthly"); // or "annual"
  const [sub, setSub] = useState(null);
  const [grad, setGrad] = useState(null);
  // Default beta=true to match backend default — prevents a flash where CTAs
  // appear enabled before /beta-state resolves.
  const [beta, setBeta] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showGrad, setShowGrad] = useState(false);
  const [gradDeclared, setGradDeclared] = useState(false);

  useEffect(() => {
    api.get("/billing/beta-state").then(({ data }) => setBeta(data.beta_mode)).catch(() => setBeta(false));
    if (user) {
      api.get("/billing/me").then(({ data }) => setSub(data)).catch(() => {});
      api.get("/billing/grad-status").then(({ data }) => setGrad(data)).catch(() => {});
    }
  }, [user]);

  const subscribe = async () => {
    if (!user) {
      navigate("/register");
      return;
    }
    setBusy(true);
    try {
      const plan_id = billing === "annual" ? "platinum_annual" : "platinum_monthly";
      const { data } = await api.post("/billing/checkout", {
        plan_id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const subscribeGrad = async () => {
    if (!user) { navigate("/register"); return; }
    if (!gradDeclared) {
      toast.error("Please tick the honour-system box first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: "platinum_grad",
        origin_url: window.location.origin,
        grad_declared: true,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const startTrial = async () => {
    setBusy(true);
    try {
      await api.post("/billing/start-trial");
      toast.success("Your 7-day Platinum trial is live");
      navigate("/diary");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const annual = billing === "annual";
  const price = annual ? "£149" : "£14.99";
  const period = annual ? "/year" : "/month";
  const sub_label = annual ? "billed annually · 2 months free" : "billed monthly";

  const isPlatinum = sub?.is_platinum;
  const isTrial = sub?.tier === "platinum_trial";
  const isGrandfathered = sub?.tier === "grandfathered";

  return (
    <div className="min-h-screen bg-ivory">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="pricing-page">
        <div className="text-center mb-10">
          <div className="label-overline inline-flex items-center gap-2"><Sparkles size={12} /> Pricing</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-2">Simple, fair, musician-friendly.</h1>
          <p className="text-ink-600 mt-3 max-w-xl mx-auto">Free for life for individual musicians. Platinum for orchestras, conductors and ensemble managers who run the gigs.</p>
        </div>

        {beta && (
          <div className="mb-8 rounded-2xl border border-sage-200 bg-sage-50 px-5 py-4 text-center" data-testid="beta-banner">
            <div className="font-heading text-lg font-semibold text-sage-800">Phase 1 beta — free for everyone</div>
            <p className="text-sm text-sage-700 mt-1">
              We're validating the core diary + dep workflow before wider rollout. Musicians get
              instrument-scoped dep finding; bookers and conductors get cross-instrument search and
              event creation. Advanced ensemble-management tools (sectional broadcasts, payroll, multi-org calendars)
              ship as the <strong>Ensemble Infrastructure</strong> tier post-beta — nobody is charged today.
            </p>
          </div>
        )}

        {/* Status banner */}
        {sub && (
          <div className="text-center mb-6 text-sm">
            {isGrandfathered && (
              <span className="pill bg-amber-soft text-amber-deep" data-testid="status-grandfathered"><Star size={12} /> Early-adopter Platinum — free forever</span>
            )}
            {isTrial && (
              <span className="pill bg-sage-100 text-sage-700" data-testid="status-trial">Trial active — ends {new Date(sub.subscription_ends_at).toLocaleDateString()}</span>
            )}
            {isPlatinum && !isTrial && !isGrandfathered && (
              <span className="pill bg-sage-100 text-sage-700" data-testid="status-platinum">Platinum active — renews {sub.subscription_ends_at ? new Date(sub.subscription_ends_at).toLocaleDateString() : ""}</span>
            )}
            {!isPlatinum && sub?.tier === "free" && (
              <span className="pill bg-ivory-100 text-ink-700" data-testid="status-free">You're on Free</span>
            )}
          </div>
        )}

        {/* Billing toggle */}
        <div className="flex justify-center mb-8">
          <div className="flex gap-1 bg-ivory-100 rounded-full p-1">
            <button onClick={() => setBilling("monthly")} className={`rounded-full px-4 py-2 text-sm font-medium ${!annual ? "bg-white shadow-soft text-ink-900" : "text-ink-600"}`} data-testid="billing-monthly">Monthly</button>
            <button onClick={() => setBilling("annual")} className={`rounded-full px-4 py-2 text-sm font-medium ${annual ? "bg-white shadow-soft text-ink-900" : "text-ink-600"}`} data-testid="billing-annual">Annual <span className="text-sage-600">· save 17%</span></button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Free */}
          <article className="card-base p-6 sm:p-8 flex flex-col" data-testid="tier-free-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-sage-100 text-sage-600 grid place-items-center"><Music2 size={20} /></div>
              <div>
                <div className="label-overline">Musician</div>
                <h3 className="font-heading text-xl font-semibold">Free</h3>
              </div>
            </div>
            <div className="mb-2">
              <span className="font-heading text-5xl font-bold tracking-tight">£0</span>
              <span className="text-ink-500 text-sm ml-2">forever</span>
            </div>
            <p className="text-ink-600 text-sm mb-5">Everything an individual musician needs to get found and confirm gigs.</p>
            <ul className="space-y-2 text-sm flex-1">
              {FREE_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2"><Check size={16} className="text-sage-500 mt-0.5 shrink-0" /><span>{b}</span></li>
              ))}
            </ul>
            <button onClick={() => navigate(user ? "/diary" : "/register")} className="btn-secondary w-full justify-center mt-6" data-testid="free-cta">
              {user ? "Open AMDS" : "Get started — it's free"}
            </button>
          </article>

          {/* Platinum */}
          <article className="card-base p-6 sm:p-8 flex flex-col relative overflow-hidden ring-2 ring-sage-500" data-testid="tier-platinum-card">
            <div className="absolute top-4 right-4 pill bg-terracotta-500 text-white">Most popular</div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-sage-500 text-white grid place-items-center"><Building size={20} /></div>
              <div>
                <div className="label-overline">Ensemble / Conductor</div>
                <h3 className="font-heading text-xl font-semibold">Platinum</h3>
              </div>
            </div>
            <div className="mb-2">
              <span className="font-heading text-5xl font-bold tracking-tight">{price}</span>
              <span className="text-ink-500 text-sm ml-2">{period}</span>
            </div>
            <p className="text-ink-600 text-sm mb-5">{sub_label}. 7-day free trial, cancel any time.</p>
            <ul className="space-y-2 text-sm flex-1">
              <li className="flex items-start gap-2 font-medium"><Check size={16} className="text-sage-500 mt-0.5 shrink-0" /><span>Everything in Free</span></li>
              {PLATINUM_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-2"><Check size={16} className="text-sage-500 mt-0.5 shrink-0" /><span>{b}</span></li>
              ))}
            </ul>
            <div className="mt-6 space-y-2">
              {!isPlatinum && !sub?.trial_used && !beta && (
                <button onClick={startTrial} disabled={busy} className="btn-secondary w-full justify-center" data-testid="trial-cta">Start 7-day free trial</button>
              )}
              <button
                onClick={subscribe}
                disabled={busy || isGrandfathered || beta}
                className="btn-primary w-full justify-center"
                data-testid="platinum-cta"
              >
                {beta
                  ? "Free during beta — no charge"
                  : isGrandfathered
                    ? "You're grandfathered — thank you!"
                    : (busy ? "Loading…" : (<>Subscribe to Platinum <ArrowRight size={16} /></>))}
              </button>
            </div>
          </article>
        </div>

        {/* Conservatoire Grad rate */}
        {!isGrandfathered && (
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setShowGrad((v) => !v)}
              className="w-full text-left card-base p-5 sm:p-6 flex items-center gap-4 hover:shadow-soft transition"
              data-testid="grad-toggle"
            >
              <div className="w-10 h-10 rounded-xl bg-terracotta-50 text-terracotta-600 grid place-items-center shrink-0">
                <GraduationCap size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-overline text-terracotta-700">Recent grad?</div>
                <div className="font-heading text-lg font-semibold">£3.99/mo Conservatoire Grad rate</div>
                <p className="text-ink-600 text-sm">Full Platinum, 12 months from your graduation. Honour system — no docs needed.</p>
              </div>
              <span className="text-ink-500 text-sm shrink-0">{showGrad ? "Hide" : "Show"}</span>
            </button>

            {showGrad && (
              <div className="card-base p-5 sm:p-6 mt-3 space-y-4" data-testid="grad-card">
                {grad?.active && (
                  <div className="pill bg-sage-100 text-sage-700" data-testid="grad-status-active">
                    Grad rate active — {grad.days_remaining} day{grad.days_remaining === 1 ? "" : "s"} remaining
                  </div>
                )}
                {grad?.expiring_soon && (
                  <div className="pill bg-amber-soft text-amber-deep" data-testid="grad-status-expiring">
                    Your grad rate ends in {grad.days_remaining} days — we'll email you to choose what's next.
                  </div>
                )}
                {grad && !grad.eligible && (
                  <div className="pill bg-ink-100 text-ink-600" data-testid="grad-status-used">
                    Your 12-month grad window has ended.
                  </div>
                )}

                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={gradDeclared}
                    onChange={(e) => setGradDeclared(e.target.checked)}
                    className="mt-1 h-4 w-4"
                    data-testid="grad-honour-checkbox"
                    disabled={!grad?.eligible}
                  />
                  <span>
                    I confirm I graduated from a music conservatoire within the last 12 months.
                    I understand this is an honour-system declaration; AMDS may ask for proof at any time.
                  </span>
                </label>

                <button
                  onClick={subscribeGrad}
                  disabled={busy || !gradDeclared || !grad?.eligible || beta}
                  className="btn-primary w-full justify-center"
                  data-testid="grad-cta"
                >
                  {beta ? "Free during beta — no charge" : (busy ? "Loading…" : (<>Subscribe at £3.99/mo <ArrowRight size={16} /></>))}
                </button>
                <p className="text-ink-500 text-xs">
                  After 12 months from your first grad-rate payment, your subscription reverts to
                  standard Platinum (£14.99/mo) unless you cancel. We'll email you 30 days before so
                  you can choose.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="text-center text-ink-500 text-xs mt-8">
          Prices in £ GBP. Test mode is on while we're in beta — no card is charged.
        </div>
      </div>
    </div>
  );
}
